import { type CSSProperties, useState } from 'react';
import { Flex, Heading, Text } from '@chakra-ui/react';
import { User } from '@/api/User';
import {
  LuPlus,
  LuMinus,
  LuUsers
} from 'react-icons/lu';
import { Avatar } from "@/components/ui/avatar"
import { userService } from '@/service/UserService';
import { Role } from '@/api/Role';
import RoleBadge from './RoleBadge';
import { useI18n } from "src/i18n/I18nContext";

export interface OrgChartNode {
  id: number;
  name: string;
  title: string;
  role: Role;
  children?: OrgChartNode[];
}

interface OrgNodeProps {
  node: OrgChartNode;
  organizationId: number;
}

interface OrgChartProps {
  nodes: OrgChartNode[];
  organizationId: number;
  minHeight?: CSSProperties['minHeight'];
}

export const buildOrgChartNodes = (data: User[], reportsTo: Map<number, number[]>): OrgChartNode[] => {
  const treeById = new Map<number, OrgChartNode>();
  const rootIds = new Set<number>(data.map(u => u.id));

  for (const user of data) {
    treeById.set(user.id, {
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      title: 'title',
      role: user.role,
      children: [],
    });
  }

  for (const [userId, managerIds] of reportsTo.entries()) {
    const managerId = managerIds?.[0];
    if (managerId === undefined || !treeById.has(userId) || !treeById.has(managerId)) continue;
    const managerNode = treeById.get(managerId);
    const userNode = treeById.get(userId);
    if (!managerNode || !userNode) continue;

    managerNode.children = managerNode.children ?? [];
    managerNode.children.push(userNode);
    rootIds.delete(userId);
  }

  const sortNode = (node: OrgChartNode): void => {
    if (!node.children || node.children.length === 0) return;
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.children.forEach(sortNode);
  };

  const roots = Array.from(rootIds)
    .map(id => treeById.get(id))
    .filter((node): node is OrgChartNode => node !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach(sortNode);
  return roots;
};

/**
 * ORG NODE COMPONENT
 * Recursively renders the tree structure using standard CSS for reliability.
 */
const OrgNode = ({ node, organizationId }: OrgNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  // Custom styles for Chakra-like appearance without relying on the external library
  const cardStyle: CSSProperties = {
    width: '7rem',
    padding: '.5rem',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'transform 0.2s',
  };

  const badgeStyle = {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#edf2f7',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#4a5568'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
      {/* Employee Card Wrapper */}
      <div style={{ position: 'relative' }}>
        <div style={cardStyle} className="node-card">
          {/* avatar */}
          <Avatar
            size='md'
            bg='white'
            borderless={false}
            borderWidth='1px'
            shadow='sm'
            name={node.name}
            src={userService.avatarUrlFrom(organizationId, node.id)}
          />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.875rem', color: '#1a202c' }}>
              {node.name}
            </div>
            {/* <div style={{ fontSize: '0.75rem', color: '#718096' }}>
              {node.title}
            </div> */}
            <RoleBadge size='xs' role={node.role}/>
          </div>
        </div>

        {/* Expand/Collapse Toggle Button */}
        {hasChildren && (
          <div style={{
            position: 'absolute',
            bottom: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20
          }}>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              style={{
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '50%',
                border: '2px solid white',
                backgroundColor: isExpanded ? '#3182ce' : '#38a169',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              {isExpanded ? <LuMinus size={12} /> : <LuPlus size={12} />}
            </button>
          </div>
        )}
      </div>

      {/* Connection and Children Rendering */}
      {hasChildren && isExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          {/* Vertical line from parent card */}
          <div style={{ width: '2px', height: '2rem', backgroundColor: '#cbd5e0' }} />

          {/* Container for children */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', position: 'relative', padding: '0 1rem' }}>
            {node.children.map((child, index) => {
              const isFirst = index === 0;
              const isLast = index === node.children.length - 1;
              const isOnly = node.children.length === 1;

              return (
                <div key={child.id} style={{ position: 'relative', paddingTop: '2rem' }}>
                  {/* Horizontal Connector Bridge */}
                  {!isOnly && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      height: '2px',
                      backgroundColor: '#cbd5e0',
                      left: isFirst ? '50%' : '0',
                      right: isLast ? '50%' : '0'
                    }} />
                  )}

                  {/* Vertical line down to the child card */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '2px',
                    height: '2rem',
                    backgroundColor: '#cbd5e0'
                  }} />

                  <div style={{ padding: '0 1rem' }}>
                    <OrgNode node={child} organizationId={organizationId}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const OrgChart = ({ nodes, organizationId, minHeight = '100vh' }: OrgChartProps) => {
  const { t } = useI18n();
  return (
    <div style={{
      minHeight,
      backgroundColor: '#f7fafc',
      padding: '3rem 1.5rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>

          {/* Page Header */}
          <Flex textAlign="center" direction="column" align="center" gap="1rem">
            <Heading
              as="h1"
              fontSize="1.875rem"
              fontWeight="800"
              color="#1a202c"
              m={0}
              display="flex"
              alignItems="center"
              gap="0.5rem"
            >
              <LuUsers size={32} />Organisation
            </Heading>
            <Text color="gray.600" fontSize={{ base: 'sm', md: 'md' }} lineHeight="1.6" m={0}>
              {t('organization.reportingLineDescription')}
            </Text>
          </Flex>

          {/* Main Chart Area */}
          <div style={{
            width: '100%',
            overflowX: 'auto',
            paddingBottom: '3rem',
            backgroundColor: 'white',
            borderRadius: '2rem',
            border: '1px solid #edf2f7',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '3rem'
          }}>
            {nodes.length === 0 ? (
              <Text textAlign='center' color='gray.500'>Keine Daten für Organigramm</Text>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', minWidth: 'fit-content', gap: '2rem' }}>
                {nodes.map(node => <OrgNode key={node.id} node={node} organizationId={organizationId}/>)}
              </div>
            )}
          </div>

        </div>
      </div>

      <style>{`
        .node-card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default OrgChart;
